<template>
  <div class="container mt-4" dir="rtl">
    
    <!-- 1. ترويسة الصفحة -->
    <div class="card shadow-sm border-top-primary mb-4 no-print">
      <div class="card-header bg-white py-3">
        <h5 class="mb-0 fw-bold text-primary">
          <i class="fas fa-edit ms-2"></i> تعديل طلب تظلم (إضافة مواد)
        </h5>
      </div>
      <div class="card-body">
        <form @submit.prevent="searchAppeal" class="row g-3 align-items-center">
          <div class="col-md-6">
            <label class="form-label fw-bold">رقم الجلوس</label>
            <div class="input-group">
              <input type="text" v-model="seatNumber" class="form-control" placeholder="أدخل رقم جلوس الطالب" required>
              <button class="btn btn-primary" type="submit" :disabled="loading">
                <i class="fas fa-search ms-1"></i> بحث
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>

    <!-- 2. تفاصيل الطلب (يظهر بعد البحث) -->
    <div v-if="appeal" class="row animate__animated animate__fadeIn">
      
      <!-- بيانات الطالب والمواد الحالية -->
      <div class="col-md-5 mb-4">
        <div class="card shadow-sm h-100">
          <div class="card-header bg-light">
            <h6 class="mb-0 fw-bold">بيانات الطلب الحالي (#{{ appeal.id }})</h6>
          </div>
          <div class="card-body">
            <ul class="list-group list-group-flush mb-3">
              <li class="list-group-item d-flex justify-content-between">
                <span>اسم الطالب:</span>
                <span class="fw-bold">{{ appeal.student?.name }}</span>
              </li>
              <li class="list-group-item d-flex justify-content-between">
                <span>رقم الجلوس:</span>
                <span class="fw-bold">{{ appeal.seat_number }}</span>
              </li>
              <li class="list-group-item d-flex justify-content-between">
                <span>تاريخ التقديم:</span>
                <span>{{ formatDate(appeal.created_at) }}</span>
              </li>
            </ul>

            <h6 class="fw-bold text-success border-bottom pb-2 mb-2">
              <i class="fas fa-check-circle ms-1"></i> المواد المسجلة بالفعل:
            </h6>
            <div class="d-flex flex-wrap gap-2">
              <span v-for="sub in appeal.subjects" :key="sub.id" class="badge bg-success fs-6">
                {{ sub.subject_name }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- قائمة إضافة مواد جديدة -->
      <div class="col-md-7 mb-4">
        <div class="card shadow-sm border-warning h-100">
          <div class="card-header bg-warning-subtle text-dark">
            <h6 class="mb-0 fw-bold">
              <i class="fas fa-plus-circle ms-2"></i> إضافة مواد جديدة لهذا الطلب
            </h6>
          </div>
          <div class="card-body">
            
            <div v-if="availableSubjects.length > 0">
              <p class="text-muted small mb-3">حدد المواد المراد إضافتها للتظلم:</p>
              
              <div class="row g-3">
                <div class="col-md-6" v-for="subject in availableSubjects" :key="subject.id">
                  <div class="form-check p-3 border rounded bg-light hover-shadow">
                    <input 
                      class="form-check-input float-end ms-2" 
                      type="checkbox" 
                      :value="subject.id" 
                      v-model="selectedSubjects" 
                      :id="'sub-'+subject.id"
                      style="transform: scale(1.3);"
                    >
                    <label class="form-check-label fw-bold w-100 cursor-pointer text-end pe-4" :for="'sub-'+subject.id">
                      {{ subject.name }}
                    </label>
                  </div>
                </div>
              </div>

              <div class="mt-4 pt-3 border-top text-center">
                <button 
                  @click="saveChanges" 
                  class="btn btn-dark w-50 fw-bold" 
                  :disabled="selectedSubjects.length === 0 || saving"
                >
                  <span v-if="saving" class="spinner-border spinner-border-sm ms-2"></span>
                  <i class="fas fa-save ms-2"></i> إضافة {{ selectedSubjects.length }} مواد
                </button>
              </div>
            </div>

            <div v-else class="text-center py-5 text-muted">
              <i class="fas fa-check-double fa-3x mb-3 text-success"></i>
              <p>هذا الطالب تظلم بالفعل في جميع المواد المتاحة.</p>
            </div>

          </div>
        </div>
      </div>

    </div>

  </div>
</template>

<script>

import api from '@/api/axios';

export default {
  name: "ControlEditAppeal",
  data() {
    return {
      seatNumber: '',
      loading: false,
      saving: false,
      appeal: null,
      availableSubjects: [], // المواد التي لم يخترها الطالب
      selectedSubjects: []   // المواد الجديدة التي سيختارها الأدمن
    };
  },
  methods: {
    async searchAppeal() {
      this.loading = true;
      this.appeal = null;
      this.selectedSubjects = []; // تصفير الاختيارات السابقة

      try {
        const res = await api.get(`/admin/appeal/search/${this.seatNumber}`);
        this.appeal = res.data.appeal;
        this.availableSubjects = res.data.available_subjects;
      } catch (error) {
        if (error.response && error.response.status === 404) {
          alert("لا يوجد طلب تظلم مسجل لهذا الرقم، يمكنه تسجيل طلب جديد.");
        } else {
          alert("حدث خطأ أثناء البحث");
        }
      } finally {
        this.loading = false;
      }
    },

    async saveChanges() {
      if (!confirm(`هل أنت متأكد من إضافة ${this.selectedSubjects.length} مواد وتكلفة إضافية؟`)) return;

      this.saving = true;
      try {
        await api.post(`/admin/appeal/${this.appeal.id}/add-subjects`, {
          subjects: this.selectedSubjects
        });

        alert("تمت إضافة المواد بنجاح");
        
        // إعادة تحميل البيانات لتحديث القوائم
        this.searchAppeal(); 

      } catch (error) {
        alert("فشل الحفظ");
      } finally {
        this.saving = false;
      }
    },

    formatDate(date) {
        return new Date(date).toLocaleDateString('ar-EG');
    }
  }
};
</script>

<style scoped>
.border-top-primary { border-top: 4px solid #0d6efd; }
.bg-warning-subtle { background-color: #fff3cd; }
.border-warning { border-color: #ffc107; }

/* تأثير عند المرور على المادة */
.hover-shadow:hover {
    background-color: #fff;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    border-color: #ffc107 !important;
}
.cursor-pointer { cursor: pointer; }
</style>